// Import the promises API from the main 'fs' specifier (which the server bundle
// externalizes) rather than the 'fs/promises' submodule — a `* as` namespace
// import of 'fs/promises' is mangled to `undefined` by the vite/rollup server
// build, so `fs.mkdir`/`fs.writeFile`/`fs.rename` would blow up in dist.
import { promises as fs } from 'fs';
import * as PATH from 'path';
import { randomUUID } from 'crypto';
import type { AssetNamespace } from '@gotti/core';

export interface AssetStore {
  /** Bytes for an asset, or null if it does not exist. */
  read(namespace: AssetNamespace, path: string): Promise<{ bytes: Uint8Array; mimeType: string } | null>;
  write(namespace: AssetNamespace, path: string, bytes: Uint8Array, mimeType: string): Promise<void>;
  /** Idempotent — removing a missing asset resolves successfully. */
  remove(namespace: AssetNamespace, path: string): Promise<void>;
  exists(namespace: AssetNamespace, path: string): Promise<boolean>;
}

const MIME: Record<string, string> = { '.json': 'application/json' };
function mimeFromExt(p: string): string {
  return MIME[PATH.extname(p).toLowerCase()] ?? 'application/octet-stream';
}

/** Default master-owned store: bytes on disk under <root>/<namespace>/<path>. */
export class FsAssetStore implements AssetStore {
  private readonly root: string;
  constructor(root: string) {
    this.root = PATH.resolve(root);
  }

  /** Resolve <root>/<namespace>/<rel>, rejecting any path that escapes <root>/<namespace>. */
  private abs(namespace: AssetNamespace, rel: string): string {
    if (namespace !== 'client' && namespace !== 'server') {
      throw new Error(`FsAssetStore: invalid namespace "${namespace}".`);
    }
    const ns_root = PATH.resolve(this.root, namespace);
    const abs = PATH.resolve(ns_root, rel);
    if (abs !== ns_root && !abs.startsWith(ns_root + PATH.sep)) {
      throw new Error(`FsAssetStore: path "${namespace}/${rel}" escapes the assets root.`);
    }
    return abs;
  }

  async read(namespace: AssetNamespace, path: string): Promise<{ bytes: Uint8Array; mimeType: string } | null> {
    try {
      const buf = await fs.readFile(this.abs(namespace, path));
      return { bytes: new Uint8Array(buf), mimeType: mimeFromExt(path) };
    } catch (e: any) {
      if (e?.code === 'ENOENT') return null;
      throw e;
    }
  }

  async write(namespace: AssetNamespace, path: string, bytes: Uint8Array, _mimeType: string): Promise<void> {
    const abs = this.abs(namespace, path);
    await fs.mkdir(PATH.dirname(abs), { recursive: true });
    // Atomic overwrite: write a temp sibling then rename over the target so a
    // concurrent reader never sees a half-written file.
    const tmp = `${abs}.tmp-${process.pid}-${randomUUID()}`;
    await fs.writeFile(tmp, bytes);
    await fs.rename(tmp, abs);
  }

  async remove(namespace: AssetNamespace, path: string): Promise<void> {
    try {
      await fs.unlink(this.abs(namespace, path));
    } catch (e: any) {
      if (e?.code !== 'ENOENT') throw e; // idempotent for missing; real errors propagate
    }
  }

  async exists(namespace: AssetNamespace, path: string): Promise<boolean> {
    try {
      await fs.access(this.abs(namespace, path));
      return true;
    } catch {
      return false;
    }
  }
}
